/**
 * NewPipelineModalContainer — selection + name state for the "+ New pipeline"
 * creation dialog (SE-5487).
 *
 * Owns the picked template and the typed name, and derives whether Create is
 * allowed (a non-empty name, and no create already in flight — this is the
 * empty-name guard the AC requires). It also builds the template cards (each
 * with a stable `onSelect` handler, so the pure View creates no function in
 * JSX). On Create it calls `onCreate(name, template)`; the parent performs the
 * single `CreateKanbanBoard` mutation and, for CUSTOM, opens the Stage Editor.
 * @remarks Container component — owns selection/name state only; no queries.
 */

import { memo, useCallback, useMemo, useState } from "react";

import { PIPELINE_TEMPLATE_OPTIONS } from "../../../constants/pipelineTemplates";
import NewPipelineModalView, {
  type PipelineTemplateCard,
} from "./NewPipelineModalView";

import { KanbanPipelineTemplate } from "@/generated/graphql";

/** Public props for the New Pipeline modal. */
export interface NewPipelineModalContainerProps {
  /** Whether the dialog is open. */
  readonly isOpen: boolean;
  /** Close request handler. */
  readonly onClose: () => void;
  /**
   * Create handler — receives the trimmed name and the selected template. The
   * parent owns the `CreateKanbanBoard` mutation and any post-create routing
   * (open directly; open the Stage Editor for CUSTOM).
   */
  readonly onCreate: (
    name: string,
    template: KanbanPipelineTemplate
  ) => Promise<void> | void;
  /** Whether a create is in flight (drives the button spinner + disabled state). */
  readonly isCreating?: boolean;
}

/** The default-selected template (first picker option). */
const DEFAULT_TEMPLATE: KanbanPipelineTemplate =
  PIPELINE_TEMPLATE_OPTIONS[0]!.template;

/**
 * Container for the "+ New pipeline" creation dialog.
 * @param props - {@link NewPipelineModalContainerProps}
 * @param props.isOpen - Whether the dialog is open
 * @param props.onClose - Close request handler
 * @param props.onCreate - Create handler (trimmed name + selected template)
 * @param props.isCreating - Whether a create is in flight
 * @returns The wired {@link NewPipelineModalView}.
 */
const NewPipelineModalContainer = ({
  isOpen,
  onClose,
  onCreate,
  isCreating = false,
}: NewPipelineModalContainerProps) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<KanbanPipelineTemplate>(DEFAULT_TEMPLATE);
  const [name, setName] = useState<string>("");

  const trimmedName = name.trim();
  // Empty name blocks creation (AC — empty-name-blocks-creation).
  const canCreate = trimmedName.length > 0 && !isCreating;

  const handleCreate = useCallback(() => {
    if (!trimmedName || isCreating) return;
    void onCreate(trimmedName, selectedTemplate);
  }, [trimmedName, isCreating, onCreate, selectedTemplate]);

  // Build the cards with a stable per-template `onSelect`, so the pure View
  // never creates a handler in JSX (react-perf jsx-no-new-function-as-prop).
  const cards = useMemo<readonly PipelineTemplateCard[]>(
    () =>
      PIPELINE_TEMPLATE_OPTIONS.map(option => ({
        ...option,
        isSelected: option.template === selectedTemplate,
        onSelect: () => setSelectedTemplate(option.template),
      })),
    [selectedTemplate]
  );

  return (
    <NewPipelineModalView
      isOpen={isOpen}
      onClose={onClose}
      cards={cards}
      name={name}
      onNameChange={setName}
      canCreate={canCreate}
      isCreating={isCreating}
      onCreate={handleCreate}
    />
  );
};

NewPipelineModalContainer.displayName = "NewPipelineModalContainer";

export default memo(NewPipelineModalContainer);
